import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { posthog } from 'posthog-js';
import { RootState } from '../../../../reducers';
import { USER_PACKAGE } from '../../../plan/userPackage';
import {
  getCancellationInfo,
  scheduleCancellation,
  acceptRetentionOffer,
  reactivateSubscription,
  CancellationInfo,
  SurveyResponse,
} from '../../../../services/cancellationServices';
import { trackCancelPlanButton } from '../../../plan/planSlice';
import {
  trackCancelTrial,
  trackCancelSubscription,
} from '../../../../lib/tracking';
import logger from '../../../../lib/logger';

// Module-level cache for getCancellationInfo to avoid redundant API calls
interface CancellationInfoCacheEntry {
  data: CancellationInfo;
  teamId: string;
  timestamp: number;
}

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

let cancellationInfoCache: CancellationInfoCacheEntry | null = null;

const getCachedCancellationInfo = (teamId: string): CancellationInfo | null => {
  if (
    cancellationInfoCache &&
    cancellationInfoCache.teamId === teamId &&
    Date.now() - cancellationInfoCache.timestamp < CACHE_TTL_MS
  ) {
    return cancellationInfoCache.data;
  }
  return null;
};

const setCancellationInfoCache = (
  teamId: string,
  data: CancellationInfo,
): void => {
  cancellationInfoCache = {
    data,
    teamId,
    timestamp: Date.now(),
  };
};

export const invalidateCancellationInfoCache = (): void => {
  cancellationInfoCache = null;
};

export interface UseCancellationFlowReturn {
  // Modal visibility
  showConfirmModal: boolean;
  showSurveyModal: boolean;
  showRetentionModal: boolean;
  showSuccessModal: boolean;

  // Data
  cancellationInfo: CancellationInfo | null;
  cancelDate: string;
  successMessage: string;
  cancelFailed: boolean;
  isScheduledForCancellation: boolean;
  cancellationDate: string | null;

  // Discount status
  hasActiveDiscount: boolean;

  // Reactivation
  reactivating: boolean;
  reactivateFailed: boolean;

  // Handlers
  handleCancelPlan: () => Promise<void>;
  handleConfirmProceed: () => void;
  handleSurveySubmit: (surveyResponse: SurveyResponse) => Promise<void>;
  handleAcceptRetentionOffer: () => Promise<void>;
  handleDeclineRetentionOffer: () => Promise<void>;
  handleReactivateSubscription: () => Promise<void>;
  closeAllModals: () => void;
}

export const useCancellationFlow = (): UseCancellationFlowReturn => {
  const dispatch = useDispatch();
  const { plan, team } = useSelector((state: RootState) => state);
  const { teamId } = team.selectedTeam || {};
  const {
    package: teamPackage,
    term,
    provider: subscriptionProvider,
  } = plan.subscription || {};

  const isFreeUser: boolean = teamPackage === USER_PACKAGE.FREE_LOGGEDIN;
  const isMobileProvider =
    subscriptionProvider === 'apple' || subscriptionProvider === 'android';

  const [hasActiveDiscount, setHasActiveDiscount] = useState(false);

  const [cancelFailed, setCancelFailed] = useState(false);
  const [isScheduledForCancellation, setIsScheduledForCancellation] =
    useState<boolean>(false);
  const [cancellationDate, setCancellationDate] = useState<string | null>(null);
  const [reactivating, setReactivating] = useState<boolean>(false);
  const [reactivateFailed, setReactivateFailed] = useState(false);

  // Native cancellation flow state
  const [cancellationInfo, setCancellationInfo] =
    useState<CancellationInfo | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [showRetentionModal, setShowRetentionModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [cancelDate, setCancelDate] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  // Check if subscription is scheduled for cancellation on mount
  useEffect(() => {
    const checkCancellationStatus = async () => {
      if (!teamId || isFreeUser || isMobileProvider) {
        setIsScheduledForCancellation(false);
        setCancellationDate(null);
        return;
      }

      try {
        // Try cache first for mount-time status check
        let info = getCachedCancellationInfo(teamId);
        if (!info) {
          info = await getCancellationInfo({ teamId });
          setCancellationInfoCache(teamId, info);
        }

        if (info.isAlreadyCanceled) {
          setIsScheduledForCancellation(true);
          setCancellationDate(info.cancelAt || info.currentPeriodEnd);
        } else {
          setIsScheduledForCancellation(false);
          setCancellationDate(null);
        }

        // Detect active 50% discount: paid user who is not eligible for retention
        // means they've already accepted the discount_50 offer
        if (info.subscriptionType === 'paid' && !info.eligibleForRetention) {
          setHasActiveDiscount(true);
        } else {
          setHasActiveDiscount(false);
        }
      } catch (error) {
        console.error('Failed to check cancellation status:', error);
        logger.error('Failed to check cancellation status:', error);
        // Don't throw - gracefully handle by assuming no scheduled cancellation
        setIsScheduledForCancellation(false);
        setCancellationDate(null);
        setHasActiveDiscount(false);
      }
    };

    checkCancellationStatus();
  }, [teamId, isFreeUser, isMobileProvider]);

  const performCancellation = async () => {
    if (!teamId) return;

    try {
      const result = await scheduleCancellation({ teamId });
      invalidateCancellationInfoCache();
      setCancelDate(result.cancelAt);
      setSuccessMessage(result.message);
      setShowSuccessModal(true);
      setIsScheduledForCancellation(true);
      setCancellationDate(result.cancelAt);

      // Track trial cancellation
      if (cancellationInfo?.subscriptionType === 'trial') {
        trackCancelTrial({
          plan: teamPackage,
          term,
          cancelAt: result.cancelAt,
        });
        // Track to PostHog for web analytics
        posthog.capture('trial_cancelled', {
          plan: teamPackage,
          term,
          cancelAt: result.cancelAt,
        });
      } else {
        trackCancelSubscription({
          plan: teamPackage,
          term,
          cancelAt: result.cancelAt,
        });
        // Track to PostHog for web analytics
        posthog.capture('paid_cancellation', {
          plan: teamPackage,
          term,
          cancelAt: result.cancelAt,
        });
      }
    } catch (error) {
      logger.error('Failed to cancel subscription:', error);
      setCancelFailed(true);
    }
  };

  const handleTrialCancellationFlow = async () => {
    if (!cancellationInfo || !teamId) return;

    // If eligible for trial extension, show retention offer
    if (
      cancellationInfo.eligibleForRetention &&
      cancellationInfo.retentionOffer
    ) {
      setShowRetentionModal(true);
    } else {
      // Not eligible, proceed with cancellation
      await performCancellation();
    }
  };

  const handleCancelPlan = async () => {
    dispatch(trackCancelPlanButton({ button: 'cancel-subscription' }));
    setCancelFailed(false);

    if (!teamId) {
      logger.error('teamId is not available');
      setCancelFailed(true);
      return;
    }

    try {
      // Always fetch fresh data when user explicitly clicks cancel
      const info = await getCancellationInfo({ teamId });
      setCancellationInfoCache(teamId, info);
      setCancellationInfo(info);

      // Check if already canceled
      if (info.isAlreadyCanceled) {
        setCancelDate(info.cancelAt || info.currentPeriodEnd);
        setSuccessMessage(
          'Your subscription is already scheduled for cancellation.',
        );
        setShowSuccessModal(true);
        return;
      }

      // Start the flow with confirmation modal
      setShowConfirmModal(true);
    } catch (error) {
      logger.error('Failed to start cancellation flow:', error);
      setCancelFailed(true);
    }
  };

  const handleConfirmProceed = () => {
    setShowConfirmModal(false);

    // For trial subscriptions, skip survey and go directly to retention or cancel
    if (cancellationInfo?.subscriptionType === 'trial') {
      handleTrialCancellationFlow();
    } else {
      // For paid subscriptions, show survey
      setShowSurveyModal(true);
    }
  };

  const handleSurveySubmit = async (surveyResponse: SurveyResponse) => {
    setShowSurveyModal(false);

    if (!teamId || !cancellationInfo) return;

    // Store survey response for later use
    setCancelDate(''); // Clear previous date
    setSuccessMessage(''); // Clear previous message

    // If eligible for retention offer, show it BEFORE canceling
    if (
      cancellationInfo.eligibleForRetention &&
      cancellationInfo.retentionOffer
    ) {
      // Store survey response temporarily (we'll need it if they decline the offer)
      (window as any).__pendingSurveyResponse = surveyResponse;
      setShowRetentionModal(true);
    } else {
      // Not eligible for retention, proceed with cancellation
      try {
        const result = await scheduleCancellation({ teamId, surveyResponse });
        invalidateCancellationInfoCache();
        setCancelDate(result.cancelAt);
        setSuccessMessage(result.message);
        setShowSuccessModal(true);
        setIsScheduledForCancellation(true);
        setCancellationDate(result.cancelAt);

        // Track subscription cancellation (paid subscriptions go through survey)
        trackCancelSubscription({
          plan: teamPackage,
          term,
          cancelAt: result.cancelAt,
          reason: surveyResponse?.reason,
        });
        // Track to PostHog for web analytics
        posthog.capture('paid_cancellation', {
          plan: teamPackage,
          term,
          cancelAt: result.cancelAt,
          reason: surveyResponse?.reason,
        });
      } catch (error) {
        logger.error('Failed to schedule cancellation:', error);
        setCancelFailed(true);
      }
    }
  };

  const handleAcceptRetentionOffer = async () => {
    if (!teamId || !cancellationInfo?.retentionOffer) return;

    try {
      const result = await acceptRetentionOffer({
        teamId,
        offerType: cancellationInfo.retentionOffer.type,
      });

      setShowRetentionModal(false);
      setSuccessMessage(result.message);

      // Clean up pending survey response
      delete (window as any).__pendingSurveyResponse;

      // Invalidate cache and reload subscription data
      invalidateCancellationInfoCache();
      window.location.reload();
    } catch (error) {
      logger.error('Failed to accept retention offer:', error);
      setCancelFailed(true);
      setShowRetentionModal(false);
    }
  };

  const handleDeclineRetentionOffer = async () => {
    setShowRetentionModal(false);

    if (!teamId) return;

    // For trial, we still need to cancel
    if (cancellationInfo?.subscriptionType === 'trial') {
      await performCancellation();
    } else {
      // For paid subscriptions, NOW we schedule the cancellation
      try {
        const surveyResponse = (window as any).__pendingSurveyResponse;
        const result = await scheduleCancellation({ teamId, surveyResponse });
        invalidateCancellationInfoCache();
        setCancelDate(result.cancelAt);
        setSuccessMessage(result.message);
        setShowSuccessModal(true);
        setIsScheduledForCancellation(true);
        setCancellationDate(result.cancelAt);

        // Track subscription cancellation (paid subscriptions decline retention offer)
        trackCancelSubscription({
          plan: teamPackage,
          term,
          cancelAt: result.cancelAt,
          reason: surveyResponse?.reason,
        });
        // Track to PostHog for web analytics
        posthog.capture('paid_cancellation', {
          plan: teamPackage,
          term,
          cancelAt: result.cancelAt,
          reason: surveyResponse?.reason,
        });

        // Clean up
        delete (window as any).__pendingSurveyResponse;
      } catch (error) {
        logger.error('Failed to schedule cancellation:', error);
        setCancelFailed(true);
      }
    }
  };

  const closeAllModals = () => {
    setShowConfirmModal(false);
    setShowSurveyModal(false);
    setShowRetentionModal(false);
    setShowSuccessModal(false);
    setCancellationInfo(null);
  };

  const handleReactivateSubscription = async () => {
    if (!teamId) return;

    try {
      setReactivating(true);
      setReactivateFailed(false); // Clear previous error
      const result = await reactivateSubscription({ teamId });
      invalidateCancellationInfoCache();

      // Update state
      setIsScheduledForCancellation(false);
      setCancellationDate(null);

      // Show success message (you could use a toast notification here)
      logger.info('Subscription reactivated:', result.message);

      // Reload to refresh subscription data
      window.location.reload();
    } catch (error) {
      logger.error('Failed to reactivate subscription:', error);
      setReactivateFailed(true);
    } finally {
      setReactivating(false);
    }
  };

  return {
    showConfirmModal,
    showSurveyModal,
    showRetentionModal,
    showSuccessModal,
    cancellationInfo,
    cancelDate,
    successMessage,
    cancelFailed,
    isScheduledForCancellation,
    cancellationDate,
    hasActiveDiscount,
    reactivating,
    reactivateFailed,
    handleCancelPlan,
    handleConfirmProceed,
    handleSurveySubmit,
    handleAcceptRetentionOffer,
    handleDeclineRetentionOffer,
    handleReactivateSubscription,
    closeAllModals,
  };
};
